import { useState, useEffect, type ChangeEvent } from "react";
import { getAsyncTasks } from "../../../../api";
import Paper from "@mui/material/Paper";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TablePagination from "@mui/material/TablePagination";
import TableRow from "@mui/material/TableRow";
import { Box } from "@mui/material";
import Dialog from "@mui/material/Dialog";
import CloseIcon from "@mui/icons-material/Close";
import IconButton from "@mui/material/IconButton";
import SnackbarCustom, {
  type SnackbarData,
} from "../../../../components/SnackBarCustom";
import type { AsyncTaskItem } from "../../../../types/async-task";

type ImportLogColumnId =
  | "create_at"
  | "status"
  | "complete_at"
  | "message_id";

type ImportLogColumn = {
  id: ImportLogColumnId;
  label: string;
  minWidth: string;
  align?: "left" | "right" | "center";
  isSmallScreen: boolean;
};

const columns: ImportLogColumn[] = [
  {
    id: "create_at",
    label: "インポート日時",
    minWidth: "fit-content",
    isSmallScreen: true,
  },
  {
    id: "status",
    label: "ステータス",
    minWidth: "fit-content",
    align: "left",
    isSmallScreen: true,
  },
  {
    id: "complete_at",
    label: "完了日時",
    minWidth: "fit-content",
    align: "left",
    isSmallScreen: false,
  },
  {
    id: "message_id",
    label: "メッセージ",
    minWidth: "fit-content",
    align: "left",
    isSmallScreen: false,
  },
];

export default function ImportLog() {
  const [selectedRow, setSelectedRow] = useState<AsyncTaskItem | null>(null);
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);

  const handleLocationCodeClick = (row: AsyncTaskItem) => {
    setSelectedRow(row);
    setIsPopoverOpen(true);
  };

  const handlePopoverClose = () => {
    setIsPopoverOpen(false);
  };

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const [importLogs, setImportLog] = useState<AsyncTaskItem[] | undefined>(
    []
  );
  const [totalCount, setTotalCount] = useState<number | undefined>();

  const getImportLog = async () => {
    try {
      const response = await getAsyncTasks({
        kind: "importUserMaster",
        stat: null,
        order: null,
        sort: null,
        page: page + 1,
        limit: rowsPerPage,
      });
      const data =
        response.status === "ok" ? response.async_tasks : undefined;
      const total =
        response.status === "ok" ? response.total_count : undefined;
      const status = response;
      console.log("インポート履歴取得", response);
      setImportLog(data);
      setTotalCount(total);
      setSnackBarstatus(status);
      setSnackbarOpen(true);
    } catch (error) {
      console.error("API呼び出しエラー", error);
    }
  };

  useEffect(() => {
    getImportLog();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleChangeRowsPerPage = (
    event: ChangeEvent<HTMLInputElement>
  ) => {
    setRowsPerPage(+event.target.value);
    setPage(0);
  };

  const [snackBarstatus, setSnackBarstatus] = useState<SnackbarData>();
  const [snackbarOpen, setSnackbarOpen] = useState(false);

  return (
    <Paper sx={{ width: "100%", overflow: "hidden", boxShadow: "none" }}>
      <TableContainer>
        <Table stickyHeader aria-label="sticky table">
          <TableHead>
            <TableRow>
              {columns.map((column) => (
                <TableCell
                  key={column.id}
                  align={column.align}
                  style={{ minWidth: column.minWidth }}
                  sx={{
                    padding: "8px",
                    fontWeight: "bold",
                    borderBottom: 2,
                    borderBottomColor: "outline.dark",
                    "@media screen and (max-width:900px)": {
                      display:
                        column.isSmallScreen === true ? "table-cell" : "none",
                    },
                  }}
                >
                  {column.label}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {importLogs?.map((row, index) => {
              return (
                <TableRow
                  hover
                  role="checkbox"
                  tabIndex={-1}
                  key={row.id ?? index}
                  onClick={() => handleLocationCodeClick(row)}
                  sx={{
                    pointerEvents: "none",
                    "@media screen and (max-width:900px)": {
                      cursor: "pointer",
                      pointerEvents: "auto",
                    },
                  }}
                >
                  {columns.map((column) => {
                    const value = row[column.id];
                    return (
                      <TableCell
                        key={column.id}
                        align={column.align}
                        sx={{
                          padding: "8px",
                          "@media screen and (max-width:900px)": {
                            display:
                              column.isSmallScreen === true
                                ? "table-cell"
                                : "none",
                          },
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: "32px",
                          }}
                        >
                          <>{value}</>
                        </Box>
                      </TableCell>
                    );
                  })}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
      <Dialog open={isPopoverOpen} onClose={handlePopoverClose}>
        <Box
          sx={{
            display: "flex",
            height: "56px",
            justifyContent: "space-between",
            alignItems: "center",
            padding: "8px",
            borderBottom: "2px solid #CCC",
          }}
        >
          <Box
            sx={{
              paddingLeft: "8px",
              fontWeight: "bold",
              fontSize: "20px",
            }}
          >
            アカウント詳細
          </Box>
          <IconButton
            aria-label="close"
            onClick={handlePopoverClose}
            sx={{
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
        <Table sx={{ WebkitWritingMode: "horizontal-tb" }}>
          <TableBody>
            {selectedRow &&
              columns.map((column) => {
                const value = selectedRow[column.id];
                return (
                  <TableRow role="checkbox" tabIndex={-1} key={column.id}>
                    <TableCell
                      align={column.align}
                      sx={{
                        color: "outline.dark",
                        fontWeight: "bold",
                        boxSizing: "border-box",
                        width: "calc((100vw - 64px) / 2)",
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          WebkitWritingMode: "horizontal-tb",
                          height: "100%",
                        }}
                      >
                        {column.label}
                      </Box>
                    </TableCell>
                    <TableCell
                      align={column.align}
                      sx={{
                        writingMode: "horizontal-tb",
                        boxSizing: "border-box",
                        width: "calc((100vw - 64px) / 2)",
                        overflowWrap: "break-word",
                      }}
                    >
                      <>{value}</>
                    </TableCell>
                  </TableRow>
                );
              })}
          </TableBody>
        </Table>
      </Dialog>

      <TablePagination
        rowsPerPageOptions={[10, 30, 50, 100]}
        component="div"
        count={totalCount ?? 0}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        sx={{
          ".MuiToolbar-root,.MuiInputBase-root,.MuiTablePagination-actions": {
            margin: "0 !important",
            padding: "0 !important",
          },
        }}
      />
      <SnackbarCustom
        data={snackBarstatus}
        openStatus={snackbarOpen}
        onClose={() => setSnackbarOpen(false)}
      />
    </Paper>
  );
}
